import type * as React from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { selectMembershipRenewalPayBlocked } from "store/reducers/user/UserAccountSlice";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";
import { useLogout } from "hooks/useLogout";
import { COLORS } from "components/v3/Theme/colors";
import GLButton from "components/v3/sharedComponents/GLButton";
import {
  GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES,
  GENOMIC_LIFE_COMPLETE_MEMBER_INTRO
} from "constants/genomicLifeCompleteProgramCopy";

/** When true: legacy inactive banners, HR continuation copy, no self-serve checkout. */
export const INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED = false;

const showInactiveReactivationUi = !INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED;

type InactivePlanCardProps = {
  onReactivateClick: () => void;
};

const formattedAnnualPrice = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency"
}).format(MEMBERSHIP_ANNUAL_PRICE_USD);

const InactivePlanCard: React.FC<InactivePlanCardProps> = ({
  onReactivateClick
}) => {
  const renewalPayBlocked = useSelector(selectMembershipRenewalPayBlocked);
  const logout = useLogout();
  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2
        }}
      >
        <Typography variant="body_strong_lg_black">
          Genomic Life Complete
        </Typography>
        {showInactiveReactivationUi ? (
          <Box
            sx={{
              backgroundColor: COLORS.NOTICE,
              borderRadius: "999px",
              px: 1.75,
              py: 0.5
            }}
          >
            <Typography
              component="span"
              sx={{
                color: COLORS.WHITE,
                fontSize: "0.8125rem",
                fontWeight: 600
              }}
            >
              <Box component="span" sx={{ fontStyle: "normal" }}>
                Expired
              </Box>
              <Box component="span" sx={{ fontStyle: "italic" }}>
                {" "}
                - time to reactivate
              </Box>
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: COLORS.NOTICE,
              borderRadius: "999px",
              px: 1.75,
              py: 0.5
            }}
          >
            <Typography
              component="span"
              sx={{
                color: COLORS.WHITE,
                fontSize: "0.8125rem",
                fontWeight: 600
              }}
            >
              Inactive
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5
        }}
      >
        <Typography
          component="p"
          variant="body"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            margin: 0
          }}
        >
          {INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED ? (
            <>
              Your Genomic Life Membership is part of your employee benefits
              package. If you change employers or health plans, you may opt to
              extend the benefit as an individual. Please contact your Human
              Resources Department or Genomic Life for more information.
            </>
          ) : (
            GENOMIC_LIFE_COMPLETE_MEMBER_INTRO
          )}
        </Typography>
        {showInactiveReactivationUi ? (
          <>
            {GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES.map((feature) => (
              <Typography key={feature.label} component="p" variant="body">
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {feature.label}
                </Box>{" "}
                {feature.body}
              </Typography>
            ))}
          </>
        ) : null}
      </Box>

      {showInactiveReactivationUi ? (
        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
            my: 2.5
          }}
        />
      ) : null}

      {renewalPayBlocked ? (
        <>
          {INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED ? (
            <Divider
              sx={{
                borderBottomWidth: "3px",
                borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
                my: 2.5
              }}
            />
          ) : null}
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              mb: showInactiveReactivationUi ? 2.5 : 0,
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                alignItems: { sm: "flex-end", xs: "stretch" },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 4,
                justifyContent: "flex-start",
                p: 0,
                mt: 1
              }}
            >
              <Alert
                data-testid="membership-renewal-inline-error"
                icon={
                  <InfoOutlined
                    sx={{ color: COLORS.NOTICE, fontSize: "1.35rem" }}
                  />
                }
                severity="info"
                sx={{
                  alignItems: "flex-start",
                  bgcolor: alpha(COLORS.NOTICE, 0.1),
                  borderRadius: "12px",
                  color: COLORS.DARK_GRAY_2,
                  flex: "1 1 0",
                  minWidth: 0,
                  px: 2,
                  py: 1.5,
                  textAlign: "left",
                  width: { sm: "auto", xs: "100%" },
                  "& .MuiAlert-icon": {
                    marginRight: 1.25,
                    padding: 0
                  },
                  "& .MuiAlert-message": {
                    color: COLORS.DARK_GRAY_2,
                    padding: 0,
                    width: "100%"
                  }
                }}
              >
                <Typography component="div" variant="body" sx={{ m: 0 }}>
                  Your payment seems to have worked, but something went wrong
                  updating your account status. Logging out and logging back in
                  normally fixes this. If you&apos;re still having problems,
                  please reach out so we can help resolve this issue.
                </Typography>
              </Alert>
              <GLButton
                id="membership-renewal-logout"
                buttonType="secondary-transparent"
                onClick={logout}
                text="Log out"
                sx={{
                  alignSelf: { sm: "flex-end", xs: "stretch" },
                  flexShrink: 0,
                  width: { sm: "auto", xs: "100%" }
                }}
              />
            </Box>
          </Box>
        </>
      ) : null}

      {showInactiveReactivationUi && !renewalPayBlocked ? (
        <Box
          sx={{
            alignItems: { md: "flex-end", xs: "stretch" },
            display: "flex",
            flexDirection: { md: "row", xs: "column" },
            gap: 2,
            justifyContent: "space-between"
          }}
        >
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <Typography
              variant="body_strong_lg_black"
              sx={{ color: COLORS.BLACK, fontWeight: 700, mb: 1 }}
            >
              Continue your Genomic Life membership:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.DARK_GRAY,
                fontSize: "0.875rem",
                mb: -0.5
              }}
            >
              Your yearly cost
            </Typography>
            <Typography
              variant="body_strong_lg"
              sx={{
                color: COLORS.ACCENT2,
                fontSize: "20px",
                fontWeight: 700
              }}
            >
              {formattedAnnualPrice}
            </Typography>
          </Box>
          <GLButton
            buttonType="secondary-transparent"
            onClick={onReactivateClick}
            text="Reactivate membership"
            sx={{
              alignSelf: { md: "auto", xs: "stretch" },
              boxSizing: "border-box",
              maxWidth: { md: "max-content", xs: "100%" },
              minWidth: 0,
              width: { md: "fit-content", xs: "100%" },
              "& .MuiTypography-root": {
                fontSize: "16px"
              }
            }}
          />
        </Box>
      ) : null}
    </>
  );
};

export default InactivePlanCard;
